import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import { useAuthStore } from '@/store/auth'

type FirebaseContext = {
  auth: import('firebase/auth').Auth
  firestore: import('firebase/firestore').Firestore
  authModule: typeof import('firebase/auth')
}

let firebasePromise: Promise<FirebaseContext> | null = null
let authListenerAttached = false

const ensureFirebase = async (
  cfg: ReturnType<typeof useRuntimeConfig>['public'],
  store: ReturnType<typeof useAuthStore>
) => {
  if (!firebasePromise) {
    firebasePromise = (async () => {
      const [
        appModule,
        authModule,
        firestoreModule
      ] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
        import('firebase/firestore')
      ])

      const firebaseConfig = {
        apiKey: cfg.firebaseApiKey,
        authDomain: cfg.firebaseAuthDomain,
        projectId: cfg.firebaseProjectId,
        appId: cfg.firebaseAppId,
        storageBucket: cfg.firebaseStorageBucket,
        messagingSenderId: cfg.firebaseMessagingSenderId
      }

      const app = appModule.getApps().length
        ? appModule.getApp()
        : appModule.initializeApp(firebaseConfig)
      const auth = authModule.getAuth(app)
      const firestore = firestoreModule.getFirestore(app, 'kudo-sales')

      if (!authListenerAttached) {
        authListenerAttached = true
        try {
          await authModule.setPersistence(auth, authModule.browserLocalPersistence)
        } catch (e) {
          if (import.meta.dev) console.warn('[auth] setPersistence failed', e)
        }

        let first = true
        authModule.onAuthStateChanged(auth, (fbUser) => {
          if (fbUser) {
            store.setUser({
              uid: fbUser.uid,
              email: fbUser.email || '',
              displayName: fbUser.displayName || ''
            })
          } else {
            store.clearUser()
          }
          if (first && import.meta.dev) {
            console.log('[auth] initial state:', fbUser ? fbUser.email : 'no user')
            first = false
          }
        })
      }

      return { auth, firestore, authModule }
    })()
  }

  return firebasePromise
}

export default defineNuxtPlugin(() => {
  const cfg = useRuntimeConfig().public
  const store = useAuthStore()

  const scheduleInit = () => {
    void ensureFirebase(cfg, store)
  }

  if (process.client) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(scheduleInit, { timeout: 2000 })
    } else {
      setTimeout(scheduleInit, 1500)
    }
  }

  return {
    provide: {
      firebaseAuth: () => ensureFirebase(cfg, store).then((ctx) => ctx.auth),
      firestore: () => ensureFirebase(cfg, store).then((ctx) => ctx.firestore),
      loginWithGoogle: async () => {
        const { auth, authModule } = await ensureFirebase(cfg, store)
        const provider = new authModule.GoogleAuthProvider()
        return authModule.signInWithPopup(auth, provider)
      },
      loginWithEmail: async (email: string, password: string) => {
        const { auth, authModule } = await ensureFirebase(cfg, store)
        return authModule.signInWithEmailAndPassword(auth, email, password)
      },
      registerWithEmail: async (email: string, password: string) => {
        const { auth, authModule } = await ensureFirebase(cfg, store)
        return authModule.createUserWithEmailAndPassword(auth, email, password)
      },
      logoutFirebase: async () => {
        const { auth, authModule } = await ensureFirebase(cfg, store)
        return authModule.signOut(auth)
      },
      sendPasswordResetEmail: async (email: string) => {
        const { auth, authModule } = await ensureFirebase(cfg, store)
        return authModule.sendPasswordResetEmail(auth, email)
      }
    }
  }
})
